import React, { useEffect, useState } from 'react';
import { Button, Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import FormSelect from '../../../components/form/FormSelect';
import { checkModulePermission } from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import AddEditAIPhoneNumberModal from './AddEditAIPhoneNumberModal';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  useAiPhoneNumberList,
  aiPhoneNumberDelete,
  aiPhoneNumberListGet,
  aiAgentOptionsGet,
  useAiAgentOptions,
  aiPhoneNumberDetach,
  aiPhoneNumberAttach,
  updateAiPhoneNumberList
} from '../../../store/slices/tenantSlices/configurationSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const AiPhoneNumber = () => {
  const dispatch = useDispatch();
  const aiAgentOptions = useAiAgentOptions();
  const aiPhoneNumberList = useAiPhoneNumberList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formType, setFormType] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchAiPhoneNumberList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter, pagesize: filter?.limit, pageno: filter?.page };
      delete payload?.limit;
      delete payload?.page;
      await dispatch(aiPhoneNumberListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteAiPhoneNumber = async () => {
    try {
      const response = await dispatch(aiPhoneNumberDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (aiPhoneNumberList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchAiPhoneNumberList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchAiAgentOptionsGet = async () => {
    try {
      await dispatch(aiAgentOptionsGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchAiAgentOptionsGet();
  }, []);

  useEffect(() => {
    fetchAiPhoneNumberList();
  }, [filter]);

  const handleDetach = async (phoneId) => {
    try {
      const response = await dispatch(aiPhoneNumberDetach({ phone_number_id: phoneId })).unwrap();
      if (response && response.success) {
        const newData = [...(aiPhoneNumberList?.data || [])].map((item) =>
          item.id === phoneId ? { ...item, active_bot_id: false } : item
        );
        dispatch(updateAiPhoneNumberList({ ...aiPhoneNumberList, data: newData }));
      } else {
        toast.error('Something went wrong.');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleAttach = async (phoneId, agentId) => {
    try {
      const payload = {
        phone_number_id: phoneId,
        agent_id: agentId
      };
      const response = await dispatch(aiPhoneNumberAttach(payload)).unwrap();
      if (response && response.success) {
        const newData = [...(aiPhoneNumberList?.data || [])].map((item) =>
          item.id === phoneId ? { ...item, active_bot_id: agentId } : item
        );
        dispatch(updateAiPhoneNumberList({ ...aiPhoneNumberList, data: newData }));
      } else {
        toast.error('Something went wrong.');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="AI Phone Number"
        isMultiAdd
        multiAddBtn={[
          {
            name: 'Import from Twilio',
            onClick: () => {
              setFormType('twilio');
              setOpenAddModal(true);
            },
            isDisabled: !checkModulePermission('is_add', 'AI Phone Number', userRolePermission)
          },
          {
            name: 'Import from Exotel',
            onClick: () => {
              setFormType('exotel');
              setOpenAddModal(true);
            },
            isDisabled: !checkModulePermission('is_add', 'AI Phone Number', userRolePermission)
          },
          {
            name: 'Import from SIP Trunk',
            onClick: () => {
              setFormType('sip');
              setOpenAddModal(true);
            },
            isDisabled: !checkModulePermission('is_add', 'AI Phone Number', userRolePermission)
          }
        ]}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : aiPhoneNumberList?.data?.length > 0 &&
        checkModulePermission('is_view', 'AI Phone Number', userRolePermission) ? (
        <div className="p-0 position-relative m-0">
          <div
            style={{
              height: 'calc(100vh - 210px)',
              overflowY: 'auto'
            }}>
            <div
              className="d-grid gap-3 py-2"
              style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '20px'
              }}>
              {[...(aiPhoneNumberList?.data || [])]?.map((number, index) => (
                <Card key={index} className="ai-phone-card">
                  <div className="ai-phone-card__header">
                    <h5 className="ai-phone-card__title">{number.phone_number}</h5>
                    <div className="ai-phone-card__actions" style={{ display: 'none' }}>
                      <Icon.Edit
                        size={16}
                        className={`cursor-pointer text-info ${
                          !checkModulePermission(
                            'is_view',
                            'AI Phone Number',
                            userRolePermission
                          ) && 'disabled'
                        }`}
                        onClick={() => {
                          setEditRecord(number);
                          setIsEdit(true);
                          setOpenAddModal(true);
                        }}
                      />

                      <Icon.Trash
                        size={16}
                        className={`cursor-pointer text-danger ${
                          !checkModulePermission(
                            'is_delete',
                            'AI Phone Number',
                            userRolePermission
                          ) && 'disabled'
                        }`}
                        onClick={() => setIsDeleteId(number.id)}
                      />
                    </div>
                  </div>
                  <div className="ai-phone-card__body">
                    <div className="agent-row">
                      <strong>{number.name}</strong>
                      <span>
                        {number.location} • {number.number_provider}
                      </span>
                    </div>
                  </div>
                  <div className="agent-row mt-2">
                    {number.active_bot_id ? (
                      (() => {
                        const attachedAgent = [...(aiAgentOptions || [])]?.find(
                          (a) => a.value === number.active_bot_id
                        );

                        return (
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <strong>{attachedAgent?.label || 'Unknown Agent'}</strong>
                            <Button
                              style={{ fontSize: '10px' }}
                              size="sm"
                              color="danger"
                              onClick={() => handleDetach(number.id)}>
                              Detach
                            </Button>
                          </div>
                        );
                      })()
                    ) : (
                      <FormSelect
                        className="table-select report-table phone-select"
                        isShowLabel={false}
                        name="active_bot_id"
                        value={number.active_bot_id}
                        placeholder="Select Agent"
                        options={aiAgentOptions || []}
                        onChange={(selected) =>
                          handleAttach(number.id, selected?.target?.value || '')
                        }
                      />
                    )}
                  </div>
                  <div className="ai-phone-card__footer">
                    <div className="ai-phone-card__tags">
                      <span
                        className={`agent-badge text-white ${
                          number.active_bot_id ? 'bg-info' : 'bg-secondary'
                        }`}>
                        {number.active_bot_id ? 'Active' : 'Inactive'}
                      </span>
                      <span className="agent-badge">
                        {number.is_manually_imported ? 'Imported' : 'Purchased'}
                      </span>
                    </div>
                    <span className="agent-id">ID: #{number.id}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={aiPhoneNumberList?.total}
              limit={aiPhoneNumberList?.limit}
              page={aiPhoneNumberList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchAiPhoneNumberList()}
            />
          </div>
        </div>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteAiPhoneNumber()}
      />
      <AddEditAIPhoneNumberModal
        isOpen={openAddModal}
        formType={formType}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          setFormType(null);
          if (apiCall) {
            fetchAiPhoneNumberList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default AiPhoneNumber;
